// KCC theme controller — hand-rolled, zero dependencies.
//
// Owns the single `data-theme` attribute on <html> and persists the user's
// choice to localStorage. Dark is the default and the baseline for all visual
// QA. There is no `prefers-color-scheme` auto-switching — the user's explicit
// choice always wins (style-guide.md: No Automatic Theme Switching).

import { useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "kcc-theme";
const DEFAULT_THEME: Theme = "dark";

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    // localStorage unavailable (private browsing, quota) — fall back silently.
    return DEFAULT_THEME;
  }
}

let currentTheme: Theme = DEFAULT_THEME;
const listeners = new Set<() => void>();

function applyToDocument(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Read the persisted theme and apply it to <html> before the first paint.
 * Call this once in main.tsx *before* rendering to avoid a flash of the
 * wrong theme.
 */
export function bindThemeToDocument(): void {
  currentTheme = readStoredTheme();
  applyToDocument(currentTheme);
}

/** Set the active theme, persist it, and notify subscribers. */
export function setTheme(next: Theme): void {
  currentTheme = next;
  applyToDocument(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Persistence is best-effort; never surface an error to the user.
  }
  listeners.forEach((listener) => listener());
}

/** Flip between dark and light. */
export function toggleTheme(): void {
  setTheme(currentTheme === "dark" ? "light" : "dark");
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Subscribe a component to the active theme. */
export function useTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => currentTheme,
    () => DEFAULT_THEME,
  );
}
